export type DonutColorSlice = {
  color: string
  count: number
}

function donutSegmentPath(
  start: number,
  end: number,
  outerR: number,
  innerR: number,
  cx: number,
  cy: number,
  color: string
): string {
  if (end - start <= 0) return ''
  if (end - start >= 1) end -= 0.00001

  const a0 = 2 * Math.PI * (start - 0.25)
  const a1 = 2 * Math.PI * (end - 0.25)
  const x1Outer = cx + outerR * Math.cos(a0)
  const y1Outer = cy + outerR * Math.sin(a0)
  const x2Outer = cx + outerR * Math.cos(a1)
  const y2Outer = cy + outerR * Math.sin(a1)
  const x1Inner = cx + innerR * Math.cos(a1)
  const y1Inner = cy + innerR * Math.sin(a1)
  const x2Inner = cx + innerR * Math.cos(a0)
  const y2Inner = cy + innerR * Math.sin(a0)
  const largeArc = end - start > 0.5 ? 1 : 0

  if (end - start >= 0.999) {
    const midRad = a0 + Math.PI
    const mx1Outer = cx + outerR * Math.cos(midRad)
    const my1Outer = cy + outerR * Math.sin(midRad)
    const mx1Inner = cx + innerR * Math.cos(midRad)
    const my1Inner = cy + innerR * Math.sin(midRad)
    return `<path d="M${x1Outer},${y1Outer} A${outerR},${outerR} 0 1,1 ${mx1Outer},${my1Outer} A${outerR},${outerR} 0 1,1 ${x1Outer},${y1Outer} L${x2Inner},${y2Inner} A${innerR},${innerR} 0 1,0 ${mx1Inner},${my1Inner} A${innerR},${innerR} 0 1,0 ${x2Inner},${y2Inner} Z" fill="${color}"/>`
  }

  return `<path d="M${x1Outer},${y1Outer} A${outerR},${outerR} 0 ${largeArc},1 ${x2Outer},${y2Outer} L${x1Inner},${y1Inner} A${innerR},${innerR} 0 ${largeArc},0 ${x2Inner},${y2Inner} Z" fill="${color}"/>`
}

export function resolveDonutClusterSizePx(total: number): number {
  if (total >= 1000) return 56
  if (total >= 100) return 48
  if (total >= 10) return 40
  return 36
}

export function buildDonutClusterSvg(
  slices: DonutColorSlice[],
  total: number,
  sizePx?: number
): string {
  const size = sizePx ?? resolveDonutClusterSizePx(total)
  const cx = size / 2
  const cy = size / 2
  const outerR = size / 2 - 1
  const innerR = outerR * 0.55
  const coloredTotal = slices.reduce((sum, s) => sum + s.count, 0)
  const sliceTotal = coloredTotal > 0 ? coloredTotal : total
  const fontSize = size > 44 ? 13 : size > 36 ? 12 : 11

  let offset = 0
  const segments: string[] = []
  for (const slice of slices) {
    if (slice.count <= 0) continue
    const start = offset / sliceTotal
    const end = (offset + slice.count) / sliceTotal
    segments.push(donutSegmentPath(start, end, outerR, innerR, cx, cy, slice.color))
    offset += slice.count
  }

  if (segments.length === 0) {
    segments.push(
      `<circle cx="${cx}" cy="${cy}" r="${outerR}" fill="#6366f1" opacity="0.92"/>`
    )
  }

  return `<svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
    <circle cx="${cx}" cy="${cy}" r="${outerR}" fill="white"/>
    ${segments.join('\n')}
    <circle cx="${cx}" cy="${cy}" r="${innerR}" fill="white"/>
    <text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" font-size="${fontSize}" font-weight="700" fill="#374151">${total}</text>
  </svg>`
}

export function buildDonutClusterHtml(
  colorCounts: Map<string, number>,
  total: number,
  sizePx?: number
): string {
  const size = sizePx ?? resolveDonutClusterSizePx(total)
  const slices: DonutColorSlice[] = [...colorCounts.entries()]
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([color, count]) => ({ color, count }))
  const svg = buildDonutClusterSvg(slices, total, size)
  return `<div class="gpx-donut-cluster" style="position:relative;width:${size}px;height:${size}px;line-height:0;">${svg}</div>`
}
